import type { Session } from "@wharfkit/session";
import { ABI, Serializer } from "@wharfkit/antelope";
import { chain, contracts } from "../config";

// Reads + writes for the on-chain order book contract (orderbook-contract/
// orderbook.cpp, deployed at contracts.orderbook). Every order is a plain
// token transfer to the contract with memo "buy|sell:<market>:<price>[:ioc]";
// there are no deposits or internal balances, so the site never holds funds.
//
// Prices on chain are integers: QUOTE sub-units per ONE WHOLE base token
// (1.73 WAX/TWAX → 173000000). All math here uses BigInt to avoid float drift.

export const ORDERBOOK_BUILD_HASH = "93b037f1c447b41a8594925674df5eeff638f9fe2b0e6d0af22c2ce640810e69";
export const ORDERBOOK_WASM_URL = "/orderbook/orderbook.wasm";
export const ORDERBOOK_ABI_URL = "/orderbook/orderbook.abi";
export const MAX_FEE_BPS = 100;
/** limit price used for market (ioc) buys: effectively "any price" */
const MARKET_BUY_PRICE = "9223372036854775807";

export interface ExtSym { sym: string; contract: string } // sym = "4,TWAX"
export interface ExchangeConfig {
  admin: string;
  fee_account: string;
  taker_fee_bps: number;
  paused: boolean;
  max_fills: number;
  next_market: string;
  next_order: string;
}
export interface Market {
  id: number;
  base: ExtSym;
  quote: ExtSym;
  enabled: boolean;
  min_quote: string;
  title: string;
  logo: string;
  website: string;
  last_price: string;
  volume_quote: string;
  trade_count: string;
  created: string;
}
export interface Order {
  id: string;
  owner: string;
  price: string;
  remaining: string;
  initial: string;
  placed: string;
}
export interface Trade {
  id: string;
  maker: string;
  taker: string;
  taker_side: number;
  price: string;
  base_amount: string;
  quote_amount: string;
  fee: string;
  time: string;
}
export interface Claim { id: number; amount: { quantity: string; contract: string } }

// ---------------------------------------------------------------- units

export const symCode = (s: ExtSym) => s.sym.split(",")[1];
export const symPrec = (s: ExtSym) => Number(s.sym.split(",")[0]);

/** "1.5" → 150000000n at precision 8. Throws on too many decimals / junk. */
export function toUnits(value: string, precision: number): bigint {
  const v = value.trim();
  if (!/^\d*(\.\d*)?$/.test(v) || v === "" || v === ".") throw new Error("Enter a number");
  const [whole, frac = ""] = v.split(".");
  if (frac.length > precision) throw new Error(`At most ${precision} decimal places`);
  return BigInt(whole || "0") * 10n ** BigInt(precision) + BigInt((frac + "0".repeat(precision)).slice(0, precision) || "0");
}

export function fromUnits(units: bigint | string | number, precision: number, trim = true): string {
  const u = BigInt(units);
  const neg = u < 0n;
  const abs = neg ? -u : u;
  const base = 10n ** BigInt(precision);
  const whole = abs / base;
  let frac = (abs % base).toString().padStart(precision, "0");
  if (trim) frac = frac.replace(/0+$/, "");
  return (neg ? "-" : "") + whole.toString() + (frac ? "." + frac : "");
}

export const assetString = (units: bigint, s: ExtSym) =>
  `${fromUnits(units, symPrec(s), false)} ${symCode(s)}`;

const ceilDiv = (a: bigint, b: bigint) => (a + b - 1n) / b;
/** quote sub-units for `base` sub-units at integer `price` (same rounding as the contract) */
export const costOf = (base: bigint, price: bigint, basePrec: number) => ceilDiv(base * price, 10n ** BigInt(basePrec));

// ---------------------------------------------------------------- reads

// Every chain read goes through here: 10s timeout per node, then fails over
// to the next public node, so one slow/hung RPC can't leave the UI stuck.
const RPC_NODES = [chain.url, "https://wax.greymass.com", "https://api.wax.alohaeos.com"]
  .filter((u, i, a) => a.indexOf(u) === i && (u === chain.url || chain.url.includes("wax") && !chain.url.includes("test")));

export async function rpc(path: string, body: unknown): Promise<Response> {
  let lastErr: unknown;
  for (const url of RPC_NODES) {
    try {
      const res = await fetch(`${url}/v1/chain/${path}`, {
        method: "POST",
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(10000),
      });
      // 5xx from the node for a real query error (e.g. unknown account) is a
      // valid answer — only retry on network/timeout problems or 502-504.
      if (res.status >= 502 && res.status <= 504) { lastErr = new Error(`RPC ${res.status}`); continue; }
      return res;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("Couldn't reach the WAX chain");
}

async function rows<T>(body: Record<string, unknown>): Promise<T[]> {
  const res = await rpc("get_table_rows", { json: true, code: contracts.orderbook, limit: 100, ...body });
  if (!res.ok) throw new Error(`Chain RPC error ${res.status}`);
  return ((await res.json()).rows ?? []) as T[];
}

// nodeos returns bools as 0/1 — normalise so `{x && <jsx/>}` never renders "0".
const normMarket = (m: Market): Market => ({ ...m, enabled: Boolean(m.enabled), id: Number(m.id) });

export async function getExchangeConfig(): Promise<ExchangeConfig | null> {
  const r = await rows<ExchangeConfig>({ scope: contracts.orderbook, table: "config", limit: 1 });
  return r[0] ? { ...r[0], paused: Boolean(r[0].paused), taker_fee_bps: Number(r[0].taker_fee_bps), max_fills: Number(r[0].max_fills) } : null;
}

export async function listMarkets(): Promise<Market[]> {
  return (await rows<Market>({ scope: contracts.orderbook, table: "markets", limit: 500 })).map(normMarket);
}

export async function getMarket(id: number | string): Promise<Market | null> {
  const r = await rows<Market>({ scope: contracts.orderbook, table: "markets", lower_bound: String(id), upper_bound: String(id), limit: 1 });
  return r[0] ? normMarket(r[0]) : null;
}

/** best-first (asks: lowest price first; bids: highest price first) */
export async function getBook(marketId: number, side: "bids" | "asks", limit = 100): Promise<Order[]> {
  return rows<Order>({ scope: String(marketId), table: side, index_position: 2, key_type: "i128", limit });
}

export async function getOrdersByOwner(marketId: number, side: "bids" | "asks", owner: string): Promise<Order[]> {
  return rows<Order>({
    scope: String(marketId), table: side, index_position: 3, key_type: "name",
    lower_bound: owner, upper_bound: owner, limit: 200,
  });
}

export async function getRecentTrades(marketId: number, limit = 40): Promise<Trade[]> {
  return rows<Trade>({ scope: String(marketId), table: "trades", reverse: true, limit });
}

export async function getClaims(owner: string): Promise<Claim[]> {
  return rows<Claim>({ scope: owner, table: "claims" });
}

export async function getBalanceUnits(account: string, s: ExtSym): Promise<bigint> {
  const res = await rpc("get_currency_balance", { code: s.contract, account, symbol: symCode(s) });
  if (!res.ok) return 0n;
  const arr = (await res.json()) as string[];
  if (!arr?.length) return 0n;
  return toUnits(arr[0].split(" ")[0], symPrec(s));
}

/** Looks a token up on its contract; returns "P,SYM" or null. */
export async function lookupToken(contract: string, symbol: string): Promise<{ sym: string; supply: string; issuer: string } | null> {
  const res = await rpc("get_currency_stats", { code: contract, symbol });
  if (!res.ok) return null;
  const j = await res.json();
  const row = j?.[symbol.toUpperCase()];
  if (!row) return null;
  const [amt] = String(row.supply).split(" ");
  const prec = amt.includes(".") ? amt.split(".")[1].length : 0;
  return { sym: `${prec},${symbol.toUpperCase()}`, supply: row.supply, issuer: row.issuer };
}

/** code hash of the exchange account; "missing" if the account doesn't exist.
 * Throws if the chain can't be reached. */
export async function getOrderbookCodeHash(): Promise<string> {
  const res = await rpc("get_code_hash", { account_name: contracts.orderbook });
  const j = await res.json().catch(() => ({}));
  if (typeof j.code_hash === "string") return j.code_hash;
  if (JSON.stringify(j).includes("unknown key") || JSON.stringify(j).includes("account_query_exception") || JSON.stringify(j).includes("Fail to retrieve account"))
    return "missing";
  throw new Error(`Unexpected chain response (${res.status})`);
}

interface PermLevelWeight { permission: { actor: string; permission: string }; weight: number }
interface AccountInfo {
  permissions: Array<{ perm_name: string; parent: string; required_auth: { threshold: number; keys: unknown[]; accounts: PermLevelWeight[]; waits: unknown[] } }>;
}

export async function getAccount(name: string): Promise<AccountInfo | null> {
  const res = await rpc("get_account", { account_name: name });
  if (!res.ok) return null;
  return res.json();
}

export function hasEosioCode(acct: AccountInfo | null): boolean {
  const active = acct?.permissions.find((p) => p.perm_name === "active");
  return !!active?.required_auth.accounts.some(
    (a) => a.permission.actor === contracts.orderbook && a.permission.permission === "eosio.code"
  );
}

// ---------------------------------------------------------------- writes

type Act = { account: string; name: string; authorization: Array<{ actor: string; permission: string }>; data: Record<string, unknown> };
const txid = (r: unknown) =>
  String((r as any)?.resolved?.transaction?.id ?? (r as any)?.response?.transaction_id ?? "");

// The site ships the exchange ABI, so WharfKit never has to fetch it from the
// chain (it can't before the contract is installed — that fetch fails with
// "Decoding error at root<abi>: Read past end of buffer").
let localAbi: unknown = null;
async function exchangeAbi(): Promise<unknown> {
  if (!localAbi) localAbi = await fetch(ORDERBOOK_ABI_URL).then((r) => r.json());
  return localAbi;
}

async function send(session: Session, actions: Act[]): Promise<string> {
  const usesExchange = actions.some((a) => a.account === contracts.orderbook);
  const abis = usesExchange ? [{ account: contracts.orderbook, abi: (await exchangeAbi()) as any }] : undefined;
  const r = await session.transact({ actions } as any, abis ? { abis } : undefined);
  return txid(r);
}

function adminAct(session: Session, name: string, data: Record<string, unknown>): Act {
  return { account: contracts.orderbook, name, authorization: [session.permissionLevel as any], data };
}

/**
 * Place a limit (or ioc/market) order.
 * buy:  `amount` is QUOTE sub-units to spend (sent to the contract)
 * sell: `amount` is BASE sub-units to sell
 */
export async function placeOrder(
  session: Session,
  m: Market,
  side: "buy" | "sell",
  amount: bigint,
  price: bigint | "market",
  ioc: boolean
): Promise<string> {
  const token = side === "buy" ? m.quote : m.base;
  const p = price === "market" ? (side === "buy" ? MARKET_BUY_PRICE : "1") : price.toString();
  const memo = `${side}:${m.id}:${p}${ioc || price === "market" ? ":ioc" : ""}`;
  return send(session, [
    {
      account: token.contract,
      name: "transfer",
      authorization: [session.permissionLevel as any],
      data: { from: session.actor.toString(), to: contracts.orderbook, quantity: assetString(amount, token), memo },
    },
  ]);
}

export function cancelOrder(session: Session, marketId: number, isBid: boolean, orderId: string) {
  return send(session, [adminAct(session, "cancel", { owner: session.actor.toString(), market_id: marketId, is_bid: isBid, order_id: orderId })]);
}

export function claimFunds(session: Session, claimId: number) {
  return send(session, [adminAct(session, "claim", { owner: session.actor.toString(), claim_id: claimId })]);
}

// admin (signed by the account stored in config.admin)
export const setFee = (s: Session, bps: number) => send(s, [adminAct(s, "setfee", { taker_fee_bps: bps })]);
export const setFeeAccount = (s: Session, a: string) => send(s, [adminAct(s, "setfeeacct", { fee_account: a })]);
export const setPaused = (s: Session, paused: boolean) => send(s, [adminAct(s, "setpaused", { paused })]);
export const setMaxFills = (s: Session, n: number) => send(s, [adminAct(s, "setmaxfills", { max_fills: n })]);
export const setExchangeAdmin = (s: Session, a: string) => send(s, [adminAct(s, "setadmin", { new_admin: a })]);

export interface MarketInput {
  base: ExtSym;
  quote: ExtSym;
  min_quote: string;
  title: string;
  logo: string;
  website: string;
}
export const addMarket = (s: Session, m: MarketInput) => send(s, [adminAct(s, "addmarket", { ...m })]);
export const editMarket = (
  s: Session,
  id: number,
  f: { enabled: boolean; min_quote: string; title: string; logo: string; website: string }
) => send(s, [adminAct(s, "editmarket", { market_id: id, ...f })]);
export const deleteMarket = (s: Session, id: number) => send(s, [adminAct(s, "delmarket", { market_id: id })]);

/** Refund many orders to their owners in one transaction (chunk before calling). */
export const adminCancelMany = (s: Session, marketId: number, orders: Array<{ isBid: boolean; id: string }>) =>
  send(s, orders.map((o) => adminAct(s, "admincancel", { market_id: marketId, is_bid: o.isBid, order_id: o.id })));
export const forceClose = (s: Session, marketId: number, isBid: boolean, id: string) =>
  send(s, [adminAct(s, "forceclose", { market_id: marketId, is_bid: isBid, order_id: id })]);

// ---------------------------------------------------------------- install

const toHex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

/**
 * Installs (or upgrades) the order book contract. Must be signed by the
 * contract account itself. Verifies the wasm sha256 against the build this
 * site was shipped with, then setcode + setabi, and adds eosio.code to the
 * account's active permission if it isn't there yet (needed so the contract
 * can send payouts). Optionally runs `init` in the same transaction.
 */
export async function hasExchangeAbi(): Promise<boolean> {
  const res = await rpc("get_abi", { account_name: contracts.orderbook });
  const j = await res.json().catch(() => ({}));
  return !!j?.abi?.actions?.some((a: { name: string }) => a.name === "addmarket");
}

async function waitFor(check: () => Promise<boolean>, seconds = 20) {
  for (let i = 0; i < seconds; i++) {
    if (await check().catch(() => false)) return;
    await new Promise((r) => setTimeout(r, 1000));
  }
}

/**
 * Installs (or upgrades) the order book contract. Must be signed by the
 * contract account itself. Verifies the wasm sha256 against the build this
 * site was shipped with. Runs as up to three small wallet approvals, each
 * skipped if already done, so it can simply be re-run to resume:
 *   1. setcode (the big one, on its own)
 *   2. setabi + add eosio.code to active (so the contract can pay out)
 *   3. init (admin + fee account)
 */
export async function installOrderbook(
  session: Session,
  init?: { admin: string; fee_account: string },
  onStep?: (msg: string) => void
): Promise<string> {
  const [wasmBuf, abiJson] = await Promise.all([
    fetch(ORDERBOOK_WASM_URL).then((r) => r.arrayBuffer()),
    fetch(ORDERBOOK_ABI_URL).then((r) => r.json()),
  ]);
  const digest = toHex(await crypto.subtle.digest("SHA-256", wasmBuf));
  if (digest !== ORDERBOOK_BUILD_HASH)
    throw new Error(`Downloaded contract doesn't match the verified build (got ${digest.slice(0, 12)}…). Not installing.`);
  const self = contracts.orderbook;
  const auth = [{ actor: self, permission: "active" }];
  let tx = "";

  // 1. code
  if ((await getOrderbookCodeHash()) !== ORDERBOOK_BUILD_HASH) {
    onStep?.("Step 1 of 3 — approve installing the contract code in your wallet…");
    tx = await send(session, [
      { account: "eosio", name: "setcode", authorization: auth, data: { account: self, vmtype: 0, vmversion: 0, code: toHex(wasmBuf) } },
    ]);
    onStep?.("Waiting for the chain to confirm the code…");
    await waitFor(async () => (await getOrderbookCodeHash()) === ORDERBOOK_BUILD_HASH);
  }

  // 2. abi + eosio.code
  const step2: Act[] = [];
  if (!(await hasExchangeAbi())) {
    step2.push({ account: "eosio", name: "setabi", authorization: auth, data: { account: self, abi: Serializer.encode({ object: ABI.from(abiJson) }).hexString } });
  }
  const acct = await getAccount(self);
  if (!hasEosioCode(acct)) {
    const active = acct?.permissions.find((p) => p.perm_name === "active");
    if (!active) throw new Error(`Couldn't read ${self}'s active permission`);
    const ra = active.required_auth;
    const accounts = [...ra.accounts, { permission: { actor: self, permission: "eosio.code" }, weight: ra.threshold }].sort((a, b) =>
      a.permission.actor === b.permission.actor
        ? a.permission.permission.localeCompare(b.permission.permission)
        : nameValueCompare(a.permission.actor, b.permission.actor)
    );
    step2.push({
      account: "eosio",
      name: "updateauth",
      authorization: auth,
      data: { account: self, permission: "active", parent: "owner", auth: { ...ra, accounts } },
    });
  }
  if (step2.length) {
    onStep?.("Step 2 of 3 — approve the contract interface + payout permission in your wallet…");
    tx = await send(session, step2);
    onStep?.("Waiting for the chain to confirm…");
    await waitFor(async () => (await hasExchangeAbi()) && hasEosioCode(await getAccount(self)));
  }

  // 3. init
  if (init) {
    onStep?.("Step 3 of 3 — approve initialising the exchange in your wallet…");
    tx = await send(session, [{ account: self, name: "init", authorization: auth, data: init }]);
  }
  onStep?.("");
  return tx;
}

// Antelope requires permission lists sorted by the 64-bit name value.
const CHARMAP = ".12345abcdefghijklmnopqrstuvwxyz";
function nameValue(n: string): bigint {
  let v = 0n;
  for (let i = 0; i < 13; i++) {
    const c = i < n.length ? CHARMAP.indexOf(n[i]) : 0;
    v = i < 12 ? (v << 5n) | BigInt(c & 0x1f) : (v << 4n) | BigInt(c & 0x0f);
  }
  return v;
}
const nameValueCompare = (a: string, b: string) => {
  const x = nameValue(a), y = nameValue(b);
  return x < y ? -1 : x > y ? 1 : 0;
};
